import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { s3Upload } from '@core/react-query/helpers'

export const createGeoJSONFile = (geoJSON: object, fileName: string): File => {
  const geoJSONString = JSON.stringify(geoJSON, null, 2)
  const blob = new Blob([geoJSONString], { type: 'application/geo+json' })
  return new File([blob], fileName, { type: 'application/geo+json' })
}

const projectMapsQueryKeys = createQueryKeys('project-maps', {
  byProjectMapConfiguration: ({
    projectId,
    timeout,
  }: RequestTypes['getProjectMapConfiguration']) => {
    return {
      queryKey: [projectId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/map-configuration`,
          { timeout },
        )

        return data
      },
    }
  },

  // ==================== Project Map Layers ====================
  getProjectMapLayers: ({ projectId }: RequestTypes['getProjectMapLayers']) => {
    return {
      queryKey: [projectId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/map-configuration/layers`,
        )

        return data
      },
    }
  },

  getProjectMapLayer: ({
    projectId,
    layerId,
  }: RequestTypes['getProjectMapLayer']) => {
    return {
      queryKey: [projectId, layerId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/map-configuration/layers/${layerId}`,
        )

        return data
      },
    }
  },
})

const projectMapsMutationKeys = createMutationKeys('project-maps', {
  updateProjectMapConfiguration: {
    mutationKey: [null],
    mutationFn: async ({
      projectId,
      ...rest
    }: RequestTypes['updateProjectMapConfiguration']) => {
      const { data } = await APIAxiosInstance.put(
        `projects/${projectId}/map-configuration`,
        rest,
      )

      return data
    },
  },

  createProjectMapLayer: {
    mutationKey: [null],
    mutationFn: async ({
      projectId,
      file,
      localGeoJson,
      name,
      type,
    }: RequestTypes['createProjectMapLayer']) => {
      // Upload the original file to the server
      const fileKey = await s3Upload({
        file,
        folderName: `map-layers-${projectId}`,
      })

      // Upload the geojson file to the server
      const geoJsonFile = createGeoJSONFile(localGeoJson, name)
      const geoJsonKey = await s3Upload({
        file: geoJsonFile,
        folderName: `map-layers-${projectId}`,
      })

      // Create the layer with the uploaded file keys
      const { data } = await APIAxiosInstance.post(
        `projects/${projectId}/map-configuration/layers`,
        { projectId, name, type, fileKey, geoJsonKey },
      )

      return data
    },
  },

  updateProjectMapLayer: {
    mutationKey: [null],
    mutationFn: async ({
      projectId,
      layerId,
      ...payload
    }: RequestTypes['updateProjectMapLayer']) => {
      let fileKey
      let geoJsonKey

      if (payload.file) {
        fileKey = await s3Upload({
          file: payload.file,
          folderName: `map-layers-${projectId}`,
        })
      }

      if (payload.localGeoJson) {
        const geoJsonFile = createGeoJSONFile(
          payload.localGeoJson,
          payload.name || 'layer',
        )
        geoJsonKey = await s3Upload({
          file: geoJsonFile,
          folderName: `map-layers-${projectId}`,
        })
      }

      const { data } = await APIAxiosInstance.put(
        `projects/${projectId}/map-configuration/layers/${layerId}`,
        {
          name: payload.name,
          visible: payload.visible,
          ...(fileKey && { fileKey }),
          ...(geoJsonKey && { geoJsonKey }),
        },
      )

      return data
    },
  },

  deleteProjectMapLayer: {
    mutationKey: [null],
    mutationFn: async ({
      projectId,
      layerId,
    }: RequestTypes['deleteProjectMapLayer']) => {
      const { data } = await APIAxiosInstance.delete(
        `projects/${projectId}/map-configuration/layers/${layerId}`,
      )

      return data
    },
  },

  updateProjectMapLayersOrder: {
    mutationKey: [null],
    mutationFn: async ({
      projectId,
      layerIds,
    }: RequestTypes['updateProjectMapLayersOrder']) => {
      const { data } = await APIAxiosInstance.put(
        `projects/${projectId}/map-configuration/layers/change-order`,
        { layerIds },
      )

      return data
    },
  },
})

const projectMapsKeys = mergeQueryKeys(
  projectMapsQueryKeys,
  projectMapsMutationKeys,
)['project-maps']

export { projectMapsKeys }
